import React from 'react';
import Layout from '../components/Layout';
import AIChatButton from '../components/AIChatButton';

const TermsAndConditions: React.FC = () => {
  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Terms and Conditions
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Please read these terms carefully before using our platform. By accessing or using our services, you agree to be bound by these terms.
            </p>
            <div className="mt-6">
              <a
                href="/legal"
                className="inline-block bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200"
              >
                View All Legal Information
              </a>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                Welcome to Profs Training Solutions & Profstudyhub.com
              </h2>
              <p className="text-gray-600 text-center">
                These terms govern your use of our educational platform and services.
              </p>
            </div>

            {/* Quick Navigation */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8 border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Quick Navigation
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ol className="list-decimal list-inside space-y-2 text-blue-800">
                  <li><a href="#course-content" className="hover:text-blue-600 underline transition-colors">Course Content and Access</a></li>
                  <li><a href="#student-responsibilities" className="hover:text-blue-600 underline transition-colors">Student Responsibilities</a></li>
                  <li><a href="#data-privacy" className="hover:text-blue-600 underline transition-colors">Data Privacy</a></li>
                  <li><a href="#directives-expectations" className="hover:text-blue-600 underline transition-colors">Directives and Expectations</a></li>
                </ol>
                <ol className="list-decimal list-inside space-y-2 text-blue-800" start={5}>
                  <li><a href="#course-engagement" className="hover:text-blue-600 underline transition-colors">Course Engagement</a></li>
                  <li><a href="#modifications" className="hover:text-blue-600 underline transition-colors">Modifications</a></li>
                  <li><a href="#contact-information" className="hover:text-blue-600 underline transition-colors">Contact Information</a></li>
                </ol>
              </div>
            </div>

            {/* Course Content and Access */}
            <section id="course-content" className="mb-8">
              <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-primary-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  1. Course Content and Access
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    All course content, including materials, lectures, and assessments, is the intellectual property of Profs Training Solutions.
                  </p>
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Course Materials are for personal educational purposes only and must not be shared, distributed, or reproduced without permission.
                  </p>
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Access is granted until the relevant professional exam period for the current semester.
                  </p>
                </div>
              </div>
            </section>

            {/* Student Responsibilities */}
            <section id="student-responsibilities" className="mb-8">
              <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-green-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  2. Student Responsibilities
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Complete lessons according to the provided schedule and maintain regular progress.
                  </p>
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Attend weekly online lectures via Zoom and participate actively in discussions.
                  </p>
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Participate in all assigned activities, projects, and mock exams to enhance learning.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Privacy */}
            <section id="data-privacy" className="mb-8">
              <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  3. Data Privacy
                </h3>
                <div className="text-gray-700">
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Your enrollment information is used solely for processing registration and training purposes.
                  </p>
                  <p className="flex items-start mt-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    We implement industry-standard security measures to protect your personal data.
                  </p>
                </div>
              </div>
            </section>

            {/* Directives and Expectations */}
            <section id="directives-expectations" className="mb-8">
              <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  4. Directives and Expectations
                </h3>
                <div className="text-gray-700">
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Profs may issue directives to enhance exam preparation and learning outcomes.
                  </p>
                  <p className="flex items-start mt-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Students must act responsibly during mentorship and follow all academic guidelines.
                  </p>
                </div>
              </div>
            </section>

            {/* Course Engagement */}
            <section id="course-engagement" className="mb-8">
              <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-orange-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  5. Course Engagement
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Regular assignments are mandatory for participation in live discussions and group activities.
                  </p>
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Repeated failure to complete assignments may result in disciplinary action or course suspension.
                  </p>
                </div>
              </div>
            </section>

            {/* Modifications */}
            <section id="modifications" className="mb-8">
              <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  6. Modifications
                </h3>
                <div className="text-gray-700">
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Profs reserves the right to modify these Terms at any time to reflect changes in our services or legal requirements.
                  </p>
                  <p className="flex items-start mt-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Changes will be posted on the Platform with an updated revision date.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section id="contact-information" className="mb-8">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  7. Contact Information
                </h3>
                <div className="text-gray-700">
                  <p className="mb-4">
                    If you have any questions about these Terms and Conditions, please contact us:
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-blue-300">
                    <p className="flex items-center text-blue-600 font-medium">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Email: 
                      <a 
                        href="mailto:profstudymate@email.com" 
                        className="ml-2 text-blue-600 hover:text-blue-800 underline transition-colors"
                      >
                        profstudymate@email.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Last Updated */}
            <div className="mt-12 pt-8 border-t border-gray-200 text-center">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">
                  <strong>Last updated:</strong> {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  These terms are subject to change. Please check back regularly for updates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* AI Chat Button */}
      <AIChatButton />
    </Layout>
  );
};

export default TermsAndConditions;
